import {
  keyToTitle,
  StockItem,
  StockItemUpdateFieldValue,
  StockItemUpdateObj,
} from '../../../shared-utilities/models-old/datastructures';
import {editableFields} from '../models/stock-manager-editable-fields';
import {stockManagerFieldValuesAreEqual} from './stock-manager-barcode.utils';

const DEFAULT_OMITTED_UPDATE_KEYS: string[] = [
  'originalValue',
  'isSelected',
  'isEdited',
  'supplierDetails',
];

export const STOCK_MANAGER_BLANK_FIELD_DISPLAY = '(blank)';

/**
 * Fields that change retail price 1 or its GP, and therefore the prices derived from it.
 */
export const STOCK_MANAGER_PRICE_ONE_DRIVER_FIELDS: string[] = ['sellPriIncl1', 'nominalGP', 'latestCost'];

/**
 * Returns true when the edit changed retail price 1 or the cost/GP it is calculated from.
 */
export function hasPriceOneChanged(stockItem: StockItem): boolean {
  if (!stockItem?.originalValue) {
    return false;
  }

  return STOCK_MANAGER_PRICE_ONE_DRIVER_FIELDS.some((field: string) =>
    !stockManagerFieldValuesAreEqual(
      field,
      stockItem[field as keyof StockItem],
      stockItem.originalValue[field]?.value,
    ),
  );
}

/**
 * Returns true when any user-editable stock manager field differs from its original value.
 */
export function stockManagerHasEditableFieldChanges(stockItem: StockItem): boolean {
  if (!stockItem?.originalValue) {
    return false;
  }

  return editableFields.some((field: string) =>
    !stockManagerFieldValuesAreEqual(
      field,
      stockItem[field as keyof StockItem],
      stockItem.originalValue[field]?.value,
    ),
  );
}

/**
 * Builds human-readable descriptions of editable field changes for tooltips.
 */
export function getStockManagerEditableChangedFieldDescriptions(stockItem: StockItem): string[] {
  if (!stockItem?.originalValue) {
    return [];
  }

  return editableFields
    .filter((field: string) =>
      !stockManagerFieldValuesAreEqual(
        field,
        stockItem[field as keyof StockItem],
        stockItem.originalValue[field]?.value,
      ),
    )
    .filter((field: string) => Boolean(keyToTitle[field]))
    .map((field: string) => {
      const originalVal = stockItem.originalValue[field]?.value;
      const currentValue = stockItem[field as keyof StockItem];
      const displayOriginal = formatStockManagerFieldChangeValue(field, originalVal);
      const displayCurrent = formatStockManagerFieldChangeValue(field, currentValue);
      return `${keyToTitle[field]} changed from ${displayOriginal} to ${displayCurrent}`;
    });
}

function formatStockManagerFieldChangeValue(field: string, value: unknown): string {
  if (field === 'multipleSuppliers' || field === 'multipleBarcodes' || field === '_tags') {
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return '';
  }
  if (isBlankSupplierFieldValue(field, value)) {
    return STOCK_MANAGER_BLANK_FIELD_DISPLAY;
  }
  return `${value ?? ''}`;
}

/**
 * Formats an edited cell value for display in the grid (e.g. cleared supplier shows as "(blank)").
 */
export function formatStockManagerEditedCellDisplayValue(field: string, value: unknown): string {
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  if (isBlankSupplierFieldValue(field, value)) {
    return STOCK_MANAGER_BLANK_FIELD_DISPLAY;
  }
  return `${value ?? ''}`;
}

function isBlankSupplierFieldValue(field: string, value: unknown): boolean {
  return (field === 'regularSuppCode' || field === 'suppCode') && String(value ?? '').trim() === '';
}

/**
 * Builds a stock update payload for a single edited item, including only fields that actually changed.
 */
export function buildStockItemUpdateObjFromEditedItem(
  item: StockItem,
  omittedKeys: string[] = DEFAULT_OMITTED_UPDATE_KEYS,
): StockItemUpdateObj | null {
  const original = item.originalValue;
  if (!original) {
    return null;
  }

  const changes: Record<string, StockItemUpdateFieldValue> = {};

  editableFields.forEach((key: string) => {
    if (omittedKeys.includes(key)) {
      return;
    }

    const currentValue = item[key as keyof StockItem];
    const originalValue = original[key]?.value;
    if (!stockManagerFieldValuesAreEqual(key, currentValue, originalValue)) {
      changes[key] = currentValue as StockItemUpdateFieldValue;
    }
  });

  if (Object.keys(changes).length === 0) {
    return null;
  }

  return {
    o: Object.keys(changes).reduce((oAcc: Record<string, StockItemUpdateFieldValue>, key: string) => {
      oAcc[key] = (original[key]?.value ?? '') as StockItemUpdateFieldValue;
      return oAcc;
    }, {}),
    n: changes,
    details: {[1]: original.desc?.value},
  };
}

